import type {
  RotationCalendar,
  RotationCalendarDate,
  RotationCalendarDateAnnotation,
  RotationCalendarDateAnnotationRange,
  RotationCalendarDateAnnotationSingle,
  RotationCalendarRotation,
  RotationCalendarSemester,
  RotationCycleChange,
  RotationType,
  SchoolDays,
} from "@shared/models/rotation-calendar"
import { filter, forEach, includes, map, reduce } from "lodash-es"
import { WEEKDAY_ROTATIONS } from "./create-attributes"

export function createRotationCalendarDates(
  rotationCalendar: RotationCalendar
): Array<RotationCalendarDate> {
  return generateDates({
    semesters: rotationCalendar.attributes.semesters,
    rotations: rotationCalendar.attributes.rotations,
    schoolDays: rotationCalendar.attributes.schoolDays,
    rotationType: rotationCalendar.attributes.rotationType,
    dateAnnotations: rotationCalendar.attributes.dateAnnotations,
  })
}

export function generateDates({
  semesters,
  rotations,
  dateAnnotations,
  schoolDays,
  rotationType,
}: {
  semesters: Array<RotationCalendarSemester>
  rotations: Array<RotationCalendarRotation>
  dateAnnotations: Array<RotationCalendarDateAnnotation>
  schoolDays: SchoolDays
  rotationType: RotationType
}): Array<RotationCalendarDate> {
  let semester = semesters[0]
  let startDate = dateFns.parse(semester.startDate)
  let endDate = dateFns.parse(semester.endDate)
  let overrides = calculateRcOverrides(dateAnnotations)
  let indexOfLastReset = 0
  return map(
    filter(dateFns.eachDay(startDate, endDate), (date) => {
      return includes(schoolDays, dateFns.getDay(date))
    }),
    (date: Date): RotationCalendarDate => {
      let dateString: string = dateFns.format(date, "YYYY-MM-DD")
      let rotationId: string | null
      let exception = overrides.get(dateString) || null
      if (rotationType === "weekly") {
        let weekDayInteger = dateFns.getDay(date)
        rotationId = WEEKDAY_ROTATIONS[weekDayInteger].id
      } else if (rotationType === "block") {
        /**
         * Handle Rotation Cycle
         * ---------------------
         * We track where we are in the cycle by incrementing an index and
         * resetting the index to 0 if the cycle needs to reset
         */
        if (
          exception === null ||
          exception.rotationShould === "NOT_CHANGE_AND_CYCLE_SHOULD_CONTINUE_UNINTERRUPTED"
        ) {
          // Increment like normal
          rotationId = rotations[indexOfLastReset % rotations.length].id
          indexOfLastReset++
        } else if (exception.rotationShould === "CHANGE_AND_CYCLE_SHOULD_CONTINUE_UNINTERRUPTED") {
          rotationId = exception.changedRotationId
          indexOfLastReset++
        } else if (exception.rotationShould === "CHANGE_AND_CYCLE_SHOULD_RESET") {
          // Set the rotationId to the id of the first rotation
          rotationId = rotations[0].id
          // reset the index and add one for the day we just applied
          indexOfLastReset = 1
        } else if (exception.rotationShould === "CHANGE_AND_CYCLE_SHOULD_BUMP_OVER_DISRUPTION") {
          rotationId = exception.changedRotationId
        } else {
          throw new Error("Rotation Cycle not set")
        }
      } else {
        throw new Error("Rotation Type not supported")
      }

      /**
       * Return the rotation date
       */
      return {
        id: dateString,
        type: "rotation-date-custom",
        attributes: {
          semesterId: semester.id,
          rotationId: rotationId,
          dateString: dateString,
          isOff: (exception && exception.isOff) || false,
        },
      }
    }
  )
}

function calculateRcOverrides(dateAnnotations: Array<RotationCalendarDateAnnotation>) {
  interface Override {
    rotationShould: RotationCycleChange
    changedRotationId: string | null
    isOff: boolean
  }
  type DateString = string
  let overrides: Map<DateString, Override> = new Map()

  // loop through ranges
  overrides = reduce(
    filter(
      dateAnnotations,
      (annotation) => annotation.type === "date-annotation-rotation-calendar-range"
    ),
    (acc, annotation: RotationCalendarDateAnnotationRange) => {
      let startDate = dateFns.parse(annotation.attributes.startDate)
      let endDate = dateFns.parse(annotation.attributes.endDate)
      forEach(dateFns.eachDay(startDate, endDate), (date) => {
        let dateString = dateFns.format(date, "YYYY-MM-DD")
        acc.set(dateString, {
          rotationShould: annotation.attributes.rotationShould,
          changedRotationId: null,
          isOff: annotation.attributes.isOff,
        })
      })
      return acc
    },
    overrides
  )

  // loop through singles
  return reduce(
    filter(
      dateAnnotations,
      (annotation) => annotation.type === "date-annotation-rotation-calendar-single"
    ),
    (acc, annotation: RotationCalendarDateAnnotationSingle) => {
      return acc.set(annotation.attributes.dateString, {
        isOff: annotation.attributes.isOff,
        rotationShould: annotation.attributes.rotationShould,
        changedRotationId: annotation.attributes.changedRotationId,
      })
    },
    overrides
  )
}
